import React, { useState, useMemo } from 'react';
import {
  ScatterChart, Scatter, BarChart, Bar, ReferenceDot,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell
} from 'recharts';
import acoNames from '../data/aco_names.json';
import { fmt, COLORS } from '../utils';
import ChartCard from './ChartCard';

const TRACK_OPTIONS = ['All','One-Sided','Two-Sided (Basic C-E/T1+)','Two-Sided (Enhanced/T3)','Two-Sided (Track 2)'];
const AGREE_OPTIONS = ['All','Initial','Renewal','Re-entering'];
const SIZE_OPTIONS  = ['All','Small (<5K)','Medium (5K–20K)','Large (>20K)'];
const YEARS = [2013,2014,2015,2016,2017,2018,2019,2020,2021,2022,2023,2024];
const ROWS_OPTIONS = [15, 25, 50, 100, 'All'];

function sizeLabel(n) {
  if (n == null) return 'Unknown';
  if (n < 5000)  return 'Small (<5K)';
  if (n < 20000) return 'Medium (5K–20K)';
  return 'Large (>20K)';
}

function normalizeAgree(v) {
  if (!v) return null;
  const l = v.toLowerCase();
  if (l.includes('re-ent') || l.includes('reent')) return 'Re-entering';
  if (l.includes('initial')) return 'Initial';
  if (l.includes('renewal')) return 'Renewal';
  return v;
}

function normalizeTrackGroup(r) {
  const tg = r.track_group;
  if (tg && tg !== 'Other') return tg;
  const ct = (r.current_track || '').trim().toUpperCase();
  if (ct === 'EN' || ct === 'ENHANCED')           return 'Two-Sided (Enhanced/T3)';
  if (['C','D','E','BASIC_C','BASIC_D','BASIC_E'].includes(ct)) return 'Two-Sided (Basic C-E/T1+)';
  if (['A','B','BASIC_A','BASIC_B'].includes(ct)) return 'One-Sided';
  if (ct === 'TRACK_1' || ct.includes('TRACK 1')) return 'One-Sided';
  if (ct === 'TRACK_1PLUS' || ct.includes('1+'))  return 'Two-Sided (Basic C-E/T1+)';
  if (ct === 'TRACK_3' || ct.includes('TRACK 3')) return 'Two-Sided (Enhanced/T3)';
  if (ct === 'TRACK_2' || ct.includes('TRACK 2')) return 'Two-Sided (Track 2)';
  return tg || 'Other';
}

// Build a lookup: aco_id -> years_count from aco_names
const ACO_TENURE = Object.fromEntries(acoNames.map(a => [a.aco_id, a.years_count]));

// Simple linear regression for trendline
function linearRegression(points) {
  const n = points.length;
  if (n < 3) return null;
  const sumX  = points.reduce((s, p) => s + p.x, 0);
  const sumY  = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = (n * sumX2 - sumX * sumX);
  if (!denom) return null;
  const slope     = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  const xMin = Math.min(...points.map(p => p.x));
  const xMax = Math.max(...points.map(p => p.x));
  return [{ x: xMin, y: slope * xMin + intercept }, { x: xMax, y: slope * xMax + intercept }];
}

const EarnQualTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d?.aco_name) return null;
  return (
    <div className="custom-tooltip">
      <div className="custom-tooltip-title">{d.aco_name}</div>
      <div className="custom-tooltip-row"><span>Quality Score</span><span className="custom-tooltip-val">{d.qual_score?.toFixed(1) ?? '—'}</span></div>
      <div className="custom-tooltip-row"><span>Earned / Bene</span><span className="custom-tooltip-val">{fmt.dollars(d.earn_per_capita, 2)}</span></div>
      <div className="custom-tooltip-row"><span>Savings Rate</span><span className="custom-tooltip-val">{fmt.pct(d.sav_rate)}</span></div>
      <div className="custom-tooltip-row"><span>Track</span><span className="custom-tooltip-val">{normalizeTrackGroup(d)}</span></div>
    </div>
  );
};

const GenRafTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d?.aco_name) return null;
  return (
    <div className="custom-tooltip">
      <div className="custom-tooltip-title">{d.aco_name}</div>
      <div className="custom-tooltip-row"><span>Composite RAF</span><span className="custom-tooltip-val">{d.composite_raf_py?.toFixed(3) ?? '—'}</span></div>
      <div className="custom-tooltip-row"><span>Generated / Bene</span><span className="custom-tooltip-val">{fmt.dollars(d.gen_per_capita, 2)}</span></div>
      <div className="custom-tooltip-row"><span>Savings Rate</span><span className="custom-tooltip-val">{fmt.pct(d.sav_rate)}</span></div>
      <div className="custom-tooltip-row"><span>Beneficiaries</span><span className="custom-tooltip-val">{fmt.num(d.n_ab)}</span></div>
    </div>
  );
};

export default function CohortExplorer({ perfData, navigateToACO, pinnedACO, pinACO }) {
  const [year, setYear]         = useState(2024);
  const [track, setTrack]       = useState('All');
  const [agree, setAgree]       = useState('All');
  const [size, setSize]         = useState('All');
  const [snfWaiver, setSnf]     = useState(false);
  const [minTenure, setTenure]  = useState(1);
  const [sortCol, setSortCol]   = useState('sav_rate');
  const [sortDir, setSortDir]   = useState('desc');
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [selectedACOIds, setSelectedACOIds] = useState(new Set());

  const pageSize = rowsPerPage === 'All' ? 99999 : rowsPerPage;

  const filtered = useMemo(() => {
    if (!perfData) return [];
    return perfData.filter(r => {
      if (r.is_midyear !== 0) return false;
      if (r.performance_year !== year) return false;
      if (track !== 'All' && normalizeTrackGroup(r) !== track) return false;
      if (agree !== 'All' && normalizeAgree(r.agree_type) !== agree) return false;
      if (size !== 'All' && sizeLabel(r.n_ab) !== size) return false;
      if (snfWaiver && r.snf_waiver !== 1) return false;
      const tenure = ACO_TENURE[r.aco_id] || 1;
      if (tenure < minTenure) return false;
      return true;
    });
  }, [perfData, year, track, agree, size, snfWaiver, minTenure]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const av = a[sortCol] ?? '';
    const bv = b[sortCol] ?? '';
    if (typeof av === 'string' || typeof bv === 'string') {
      const as = String(av).toLowerCase(), bs = String(bv).toLowerCase();
      return sortDir === 'desc' ? bs.localeCompare(as) : as.localeCompare(bs);
    }
    const an = av === '' ? -Infinity : av;
    const bn = bv === '' ? -Infinity : bv;
    return sortDir === 'desc' ? bn - an : an - bn;
  }), [filtered, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
    setTablePage(0);
  };

  const aggs = useMemo(() => {
    const n = filtered.length;
    if (!n) return null;
    const avg = arr => { const v = arr.filter(x => x != null); return v.length ? v.reduce((s,x) => s+x,0)/v.length : null; };
    const sum = arr => arr.filter(x => x != null).reduce((s,x) => s+x, 0);
    return {
      n,
      pctEarning: (filtered.filter(r => (r.earn_save_loss||0) > 0).length / n * 100).toFixed(1),
      avgSavRate: avg(filtered.map(r => r.sav_rate)),
      totalEarned: sum(filtered.map(r => r.earn_save_loss)),
      avgQual: avg(filtered.map(r => r.qual_score)),
      avgExpPbpm: avg(filtered.map(r => r.expense_pbpm)),
      totalBenes: sum(filtered.map(r => r.n_ab)),
    };
  }, [filtered]);

  // Pinned ACO record for current year
  const pinnedRec = useMemo(() => {
    if (!pinnedACO) return null;
    return filtered.find(r => r.aco_id === pinnedACO.aco_id) || null;
  }, [pinnedACO, filtered]);

  // Scatter 1: earned per bene vs quality
  const earnQualData = useMemo(() =>
    filtered.filter(r => r.earn_per_capita != null && r.qual_score != null),
  [filtered]);

  const earnQualTrend = useMemo(() => {
    const pts = earnQualData.map(r => ({ x: r.qual_score, y: r.earn_per_capita }));
    return linearRegression(pts);
  }, [earnQualData]);

  // Scatter 2: generated per bene vs composite RAF
  const genRafData = useMemo(() =>
    filtered.filter(r => r.gen_per_capita != null && r.composite_raf_py != null),
  [filtered]);

  const genRafTrend = useMemo(() => {
    const pts = genRafData.map(r => ({ x: r.composite_raf_py, y: r.gen_per_capita }));
    return linearRegression(pts);
  }, [genRafData]);

  // Savings rate distribution
  const distData = useMemo(() => {
    const buckets = [
      { label: '<-5%',      min: -Infinity, max: -0.05, positive: false },
      { label: '-5 to -2%', min: -0.05, max: -0.02, positive: false },
      { label: '-2 to 0%',  min: -0.02, max: 0,     positive: false },
      { label: '0 to 2%',   min: 0,     max: 0.02,  positive: true },
      { label: '2 to 5%',   min: 0.02,  max: 0.05,  positive: true },
      { label: '5 to 10%',  min: 0.05,  max: 0.10,  positive: true },
      { label: '>10%',      min: 0.10,  max: Infinity, positive: true },
    ];
    return buckets.map(b => ({
      label: b.label, positive: b.positive,
      count: filtered.filter(r => r.sav_rate != null && r.sav_rate >= b.min && r.sav_rate < b.max).length,
    }));
  }, [filtered]);

  // Track breakdown
  const trackDist = useMemo(() => {
    const counts = {};
    filtered.forEach(r => { const t = normalizeTrackGroup(r); counts[t] = (counts[t]||0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const paged = sorted.slice(tablePage * pageSize, (tablePage + 1) * pageSize);
  const totalPages = Math.ceil(sorted.length / pageSize);

  const TRACK_COLORS_MAP = {
    'One-Sided': COLORS.blue, 'Two-Sided (Track 2)': COLORS.amber,
    'Two-Sided (Basic C-E/T1+)': COLORS.teal, 'Two-Sided (Enhanced/T3)': COLORS.green, 'Other': COLORS.gray,
  };

  const selectStyle = {
    padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)',
    fontSize: 13, fontFamily: 'var(--font)', color: 'var(--gray-800)', background: 'white', cursor: 'pointer',
  };

  // ACO selection toggle
  const toggleACO = (aco_id) => {
    setSelectedACOIds(prev => {
      const next = new Set(prev);
      if (next.has(aco_id)) next.delete(aco_id); else next.add(aco_id);
      return next;
    });
  };
  const clearSelection = () => setSelectedACOIds(new Set());
  const selectAll = () => setSelectedACOIds(new Set(sorted.map(r => r.aco_id)));

  // Cohort stats for selected ACOs
  const selectedRecs = sorted.filter(r => selectedACOIds.has(r.aco_id));
  const selectedAggs = selectedRecs.length > 0 ? (() => {
    const avg = arr => { const v = arr.filter(x => x != null); return v.length ? v.reduce((s,x)=>s+x,0)/v.length : null; };
    const sum = arr => arr.filter(x => x != null).reduce((s,x)=>s+x,0);
    return {
      n: selectedRecs.length,
      pctEarning: (selectedRecs.filter(r=>(r.earn_save_loss||0)>0).length/selectedRecs.length*100).toFixed(1),
      avgSavRate: avg(selectedRecs.map(r=>r.sav_rate)),
      totalEarned: sum(selectedRecs.map(r=>r.earn_save_loss)),
      avgQual: avg(selectedRecs.map(r=>r.qual_score)),
      avgExpPbpm: avg(selectedRecs.map(r=>r.expense_pbpm)),
    };
  })() : null;

  // CSV export
  const exportCSV = () => {
    const headers = ['ACO ID','ACO Name','State','Benes','Savings Rate','Earned Savings','Earned/Bene','Quality Score','Expense PBPM','RAF','Track','Years in Program'];
    const rows = sorted.map(r => [
      r.aco_id, r.aco_name, r.aco_state || '',
      r.n_ab, r.sav_rate != null ? (r.sav_rate*100).toFixed(2)+'%' : '',
      r.earn_save_loss != null ? r.earn_save_loss.toFixed(0) : '',
      r.earn_per_capita != null ? r.earn_per_capita.toFixed(2) : '',
      r.qual_score != null ? r.qual_score.toFixed(1) : '',
      r.expense_pbpm != null ? r.expense_pbpm.toFixed(2) : '',
      r.composite_raf_py != null ? r.composite_raf_py.toFixed(3) : '',
      normalizeTrackGroup(r),
      ACO_TENURE[r.aco_id] || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `mssp-cohort-py${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const SortHeader = ({ col, label }) => (
    <th onClick={() => handleSort(col)} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600,
      color: sortCol === col ? 'var(--teal-dark)' : 'var(--gray-600)', fontSize: 11,
      textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label} {sortCol === col ? (sortDir === 'desc' ? '↓' : '↑') : ''}
    </th>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Filter bar ── */}
      <div className="chart-card">
        <div className="chart-title" style={{ marginBottom: 14 }}>Filter Cohort</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>

          {[
            { label: 'Performance Year', value: year, set: v => { setYear(Number(v)); setTablePage(0); },
              opts: YEARS.map(y => ({ value: y, label: `PY ${y}` })) },
            { label: 'Risk Track', value: track, set: v => { setTrack(v); setTablePage(0); },
              opts: TRACK_OPTIONS.map(t => ({ value: t, label: t })) },
            { label: 'Agreement Type', value: agree, set: v => { setAgree(v); setTablePage(0); },
              opts: AGREE_OPTIONS.map(a => ({ value: a, label: a })) },
            { label: 'ACO Size', value: size, set: v => { setSize(v); setTablePage(0); },
              opts: SIZE_OPTIONS.map(s => ({ value: s, label: s })) },
          ].map(f => (
            <div key={f.label}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4,
                textTransform: 'uppercase', letterSpacing: '0.05em' }}>{f.label}</div>
              <select value={f.value} onChange={e => f.set(e.target.value)} style={selectStyle}>
                {f.opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          ))}

          {/* Tenure filter */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4,
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Min Years in Program
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="range" min={1} max={12} step={1} value={minTenure}
                onChange={e => { setTenure(Number(e.target.value)); setTablePage(0); }}
                style={{ width: 100 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', minWidth: 24 }}>
                {minTenure}{minTenure === 1 ? '' : '+'}
              </span>
              {minTenure > 1 && (
                <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                  ({acoNames.filter(a => a.years_count >= minTenure).length} eligible ACOs)
                </span>
              )}
            </div>
          </div>

          {/* SNF waiver */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4,
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>SNF Waiver</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)',
              fontSize: 13, color: 'var(--gray-800)' }}>
              <input type="checkbox" checked={snfWaiver}
                onChange={e => { setSnf(e.target.checked); setTablePage(0); }} />
              Only SNF Waiver
            </label>
          </div>
        </div>
      </div>

      {/* ── KPI strip ── */}
      {aggs && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
          {[
            { label: 'ACOs in cohort',      value: fmt.num(aggs.n) },
            { label: '% Earning savings',   value: `${aggs.pctEarning}%` },
            { label: 'Avg savings rate',    value: fmt.pct(aggs.avgSavRate) },
            { label: 'Total earned savings',value: fmt.dollars(aggs.totalEarned, 2) },
            { label: 'Avg quality score',   value: aggs.avgQual != null ? aggs.avgQual.toFixed(1) : '—' },
            { label: 'Avg expense PBPM',    value: aggs.avgExpPbpm != null
              ? `$${Number(aggs.avgExpPbpm).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—' },
            { label: 'Total beneficiaries', value: fmt.shortNum(aggs.totalBenes) },
          ].map(k => (
            <div className="card" key={k.label}>
              <div className="card-title">{k.label}</div>
              <div className="card-value" style={{ fontSize: '1.4rem' }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Track distribution pills ── */}
      {trackDist.length > 0 && aggs && (
        <div className="chart-card" style={{ padding: '14px 20px' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)',
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>Track mix:</span>
            {trackDist.sort((a,b) => b.value - a.value).map(t => {
              const pct = (t.value / aggs.n * 100).toFixed(0);
              const c = TRACK_COLORS_MAP[t.name] || COLORS.gray;
              return (
                <div key={t.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: c }} />
                  <span style={{ fontSize: 12, color: 'var(--gray-700)' }}>{t.name}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{t.value}</span>
                  <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Selected ACO comparison / cohort panel ── */}
      {selectedACOIds.size > 0 && selectedAggs && (() => {
        const isSideBySide = selectedRecs.length >= 2 && selectedRecs.length <= 5;

        // Metrics for side-by-side view
        const compMetrics = [
          { label: 'State',          fmt: r => r.aco_state || '—',                       bar: false },
          { label: 'Track',          fmt: r => normalizeTrackGroup(r),                    bar: false },
          { label: 'Benes',          fmt: r => fmt.num(r.n_ab),                           bar: false },
          { label: 'Savings Rate',   fmt: r => fmt.pct(r.sav_rate),
            val: r => r.sav_rate,    hi: true,  bar: true,
            range: () => { const v = selectedRecs.map(r=>r.sav_rate).filter(x=>x!=null); return [Math.min(...v), Math.max(...v)]; } },
          { label: 'Earned Savings', fmt: r => fmt.dollars(r.earn_save_loss, 2),
            val: r => r.earn_save_loss, hi: true, bar: true,
            range: () => { const v = selectedRecs.map(r=>r.earn_save_loss).filter(x=>x!=null); return [Math.min(...v), Math.max(...v)]; } },
          { label: 'Earned/Bene',    fmt: r => r.earn_per_capita != null ? `$${Number(r.earn_per_capita).toFixed(2)}` : '—',
            val: r => r.earn_per_capita, hi: true, bar: true,
            range: () => { const v = selectedRecs.map(r=>r.earn_per_capita).filter(x=>x!=null); return [Math.min(...v), Math.max(...v)]; } },
          { label: 'Quality Score',  fmt: r => r.qual_score?.toFixed(1) ?? '—',
            val: r => r.qual_score,  hi: true,  bar: true,
            range: () => { const v = selectedRecs.map(r=>r.qual_score).filter(x=>x!=null); return [Math.min(0,...v), Math.max(...v)]; } },
          { label: 'Expense PBPM',   fmt: r => r.expense_pbpm != null ? `$${Number(r.expense_pbpm).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}` : '—',
            val: r => r.expense_pbpm, hi: false, bar: true,
            range: () => { const v = selectedRecs.map(r=>r.expense_pbpm).filter(x=>x!=null); return [Math.min(...v), Math.max(...v)]; } },
          { label: 'Composite RAF',  fmt: r => r.composite_raf_py?.toFixed(3) ?? '—',
            val: r => r.composite_raf_py, hi: false, bar: false },
          { label: 'Yrs in Program', fmt: r => ACO_TENURE[r.aco_id] || '—',             bar: false },
        ];

        const exportCSV = () => {
          const headers = ['ACO ID','ACO Name','Savings Rate','Earned Savings','Quality Score','Expense PBPM','Track'];
          const rows = selectedRecs.map(r => [
            r.aco_id, r.aco_name,
            r.sav_rate != null ? (r.sav_rate*100).toFixed(2)+'%' : '',
            r.earn_save_loss != null ? r.earn_save_loss.toFixed(0) : '',
            r.qual_score != null ? r.qual_score.toFixed(1) : '',
            r.expense_pbpm != null ? r.expense_pbpm.toFixed(2) : '',
            normalizeTrackGroup(r),
          ]);
          const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
          const blob = new Blob([csv],{type:'text/csv'});
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a'); a.href=url;
          a.download=`mssp-comparison-py${year}.csv`; a.click();
          URL.revokeObjectURL(url);
        };

        return (
          <div className="chart-card" style={{ border: '2px solid var(--teal)', padding: 0, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', background: 'var(--teal-light)', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div className="chart-title" style={{ color: 'var(--teal-dark)', marginBottom: 2 }}>
                  {isSideBySide ? 'ACO Side-by-Side Comparison' : `Selected Cohort — ${selectedACOIds.size} ACOs`}
                </div>
                <div className="chart-sub">
                  {isSideBySide
                    ? `Comparing ${selectedRecs.length} ACOs · PY ${year}`
                    : selectedRecs.map(r => r.aco_name || r.aco_id).slice(0,5).join(', ') + (selectedRecs.length > 5 ? ` +${selectedRecs.length - 5} more` : '')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {isSideBySide && (
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                    Select 2–5 ACOs for side-by-side · 6+ shows aggregate
                  </div>
                )}
                <button onClick={exportCSV}
                  style={{ padding: '5px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--teal)',
                    background: 'white', color: 'var(--teal-dark)', cursor: 'pointer',
                    fontSize: 12, fontFamily: 'var(--font)', fontWeight: 600 }}>
                  ↓ Export
                </button>
                <button onClick={clearSelection}
                  style={{ padding: '5px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--gray-300)',
                    background: 'white', color: 'var(--gray-600)', cursor: 'pointer',
                    fontSize: 12, fontFamily: 'var(--font)' }}>
                  Clear ✕
                </button>
              </div>
            </div>

            {isSideBySide ? (
              /* ── Side-by-side comparison table ── */
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--navy)' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                        color: 'rgba(255,255,255,0.6)', fontSize: 11, textTransform: 'uppercase',
                        letterSpacing: '0.05em', width: 130 }}>Metric</th>
                      {selectedRecs.map((r, ci) => (
                        <th key={r.aco_id} style={{ padding: '10px 14px', textAlign: 'left',
                          fontWeight: 700, color: 'white', fontSize: 12, minWidth: 160 }}>
                          <div style={{ marginBottom: 2 }}>{r.aco_name || r.aco_id}</div>
                          <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--teal)', opacity: 0.9 }}>
                            {r.aco_id}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {compMetrics.map((m, mi) => {
                      const range = m.bar ? m.range() : null;
                      const span = range ? Math.max(0.001, range[1] - range[0]) : 1;
                      return (
                        <tr key={m.label} style={{ background: mi % 2 === 0 ? 'white' : 'var(--gray-50)',
                          borderBottom: '1px solid var(--gray-100)' }}>
                          <td style={{ padding: '9px 14px', fontWeight: 600, fontSize: 12,
                            color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {m.label}
                          </td>
                          {selectedRecs.map((r, ci) => {
                            const val = m.val ? m.val(r) : null;
                            const isHi = m.bar && m.hi && val != null && val === Math.max(...selectedRecs.map(x => m.val(x) ?? -Infinity));
                            const isLo = m.bar && !m.hi && val != null && val === Math.min(...selectedRecs.map(x => m.val(x) ?? Infinity));
                            const barPct = m.bar && val != null ? Math.max(0, Math.min(100, ((val - range[0]) / span) * 100)) : 0;
                            return (
                              <td key={r.aco_id} style={{ padding: '9px 14px', verticalAlign: 'middle' }}>
                                <div style={{ fontWeight: 700, fontSize: 13,
                                  color: isHi || isLo ? 'var(--teal-dark)' : 'var(--navy)',
                                  marginBottom: m.bar && val != null ? 4 : 0 }}>
                                  {(isHi || isLo) && <span style={{ marginRight: 4 }}>{isHi ? '↑' : '↓'}</span>}
                                  {m.fmt(r)}
                                </div>
                                {m.bar && val != null && (
                                  <div style={{ height: 4, borderRadius: 2, background: 'var(--gray-200)', width: '100%' }}>
                                    <div style={{ height: 4, borderRadius: 2, width: `${barPct}%`,
                                      background: isHi || isLo ? 'var(--teal)' : 'var(--gray-400)',
                                      transition: 'width 0.3s ease' }} />
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              /* ── Aggregate summary (6+ ACOs) ── */
              <div style={{ padding: '12px 16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  {[
                    { label: '% Earning Savings',  value: `${selectedAggs.pctEarning}%` },
                    { label: 'Avg Savings Rate',   value: fmt.pct(selectedAggs.avgSavRate) },
                    { label: 'Total Earned',       value: fmt.dollars(selectedAggs.totalEarned, 2) },
                    { label: 'Avg Quality Score',  value: selectedAggs.avgQual != null ? selectedAggs.avgQual.toFixed(1) : '—' },
                    { label: 'Avg Expense PBPM',   value: selectedAggs.avgExpPbpm != null ? `$${Number(selectedAggs.avgExpPbpm).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}` : '—' },
                    { label: 'ACOs in Selection',  value: fmt.num(selectedAggs.n) },
                  ].map(k => (
                    <div key={k.label} style={{ background: 'white', borderRadius: 'var(--radius)',
                      padding: '8px 12px', border: '1px solid var(--gray-200)' }}>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase',
                        letterSpacing: '0.05em', fontWeight: 500 }}>{k.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginTop: 2 }}>{k.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray-500)' }}>
                  Tip: Select 2–5 ACOs for a detailed side-by-side comparison. ↑ = best in group · ↓ = lowest in group
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Scatter charts ── */}
      <div className="chart-grid-2">

        {/* Scatter 1: earned per bene vs quality */}
        <ChartCard title="Earned Savings per Beneficiary vs. Quality Score"
          sub={`Each dot = one ACO · PY ${year} · line = trend`}
          filename={`mssp-cohort-earned-vs-quality-py${year}`}>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 8, right: 20, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="qual_score" name="Quality" type="number" domain={[50, 105]}
                label={{ value: 'Quality Score', position: 'insideBottom', offset: -4, fontSize: 11 }}
                tick={{ fontSize: 11 }} />
              <YAxis dataKey="earn_per_capita" name="Earned/Bene" type="number"
                tickFormatter={v => `$${Math.abs(v) >= 1000 ? `${(v/1000).toFixed(1)}K` : Math.round(v)}`}
                tick={{ fontSize: 11 }} width={52} />
              <Tooltip content={<EarnQualTooltip />} />
              <ReferenceLine y={0} stroke="var(--gray-400)" strokeWidth={1} />
              <Scatter data={earnQualData}
                shape={(props) => {
                  const isPinned = pinnedRec && props.payload?.aco_id === pinnedRec.aco_id;
                  const opacity = pinnedRec ? (isPinned ? 1 : 0.15) : 0.7;
                  const r = isPinned ? 8 : 4;
                  const fill = isPinned
                    ? (props.payload.earn_per_capita > 0 ? COLORS.teal : COLORS.coral)
                    : (props.payload.earn_per_capita > 0 ? COLORS.teal : COLORS.coral);
                  return (
                    <circle cx={props.cx} cy={props.cy} r={r} fill={fill}
                      opacity={opacity}
                      stroke={isPinned ? 'white' : 'none'}
                      strokeWidth={isPinned ? 2 : 0} />
                  );
                }}>
              </Scatter>
              {pinnedRec?.qual_score != null && pinnedRec?.earn_per_capita != null && (
                <ReferenceDot
                  x={pinnedRec.qual_score} y={pinnedRec.earn_per_capita}
                  r={0} fill="none" stroke="none"
                  label={{ value: '▶ ' + (pinnedRec.aco_name || '').split(' ').slice(0,3).join(' '),
                    position: 'top', fontSize: 11, fontWeight: 700, fill: 'var(--teal-dark)',
                    style: { pointerEvents: 'none' } }}
                />
              )}
              {earnQualTrend && (
                <Scatter data={earnQualTrend} dataKey="y"
                  line={{ stroke: COLORS.navy, strokeWidth: 2, strokeDasharray: '6 3' }}
                  shape={() => null} legendType="none" />
              )}
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
            🟢 Teal = earned savings &nbsp;🟠 Coral = did not earn &nbsp;— Navy = trend
          </div>
        </ChartCard>

        {/* Scatter 2: generated per bene vs RAF */}
        <ChartCard title="Generated Savings per Beneficiary vs. Risk Score (RAF)"
          sub={`Does higher patient risk correlate with more generated savings? · PY ${year}`}
          filename={`mssp-cohort-generated-vs-raf-py${year}`}>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 8, right: 20, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="composite_raf_py" name="RAF" type="number"
                domain={['auto', 'auto']}
                label={{ value: 'Composite RAF Score', position: 'insideBottom', offset: -4, fontSize: 11 }}
                tick={{ fontSize: 11 }} tickFormatter={v => v?.toFixed(2)} />
              <YAxis dataKey="gen_per_capita" name="Generated/Bene" type="number"
                tickFormatter={v => `$${Math.abs(v) >= 1000 ? `${(v/1000).toFixed(1)}K` : Math.round(v)}`}
                tick={{ fontSize: 11 }} width={52} />
              <Tooltip content={<GenRafTooltip />} />
              <ReferenceLine y={0} stroke="var(--gray-400)" strokeWidth={1} />
              <Scatter data={genRafData}
                shape={(props) => {
                  const isPinned = pinnedRec && props.payload?.aco_id === pinnedRec.aco_id;
                  const opacity = pinnedRec ? (isPinned ? 1 : 0.15) : 0.7;
                  const r = isPinned ? 8 : 4;
                  const fill = props.payload.gen_per_capita > 0 ? COLORS.blue : COLORS.coral;
                  return (
                    <circle cx={props.cx} cy={props.cy} r={r} fill={fill}
                      opacity={opacity}
                      stroke={isPinned ? 'white' : 'none'}
                      strokeWidth={isPinned ? 2 : 0} />
                  );
                }}>
              </Scatter>
              {pinnedRec?.composite_raf_py != null && pinnedRec?.gen_per_capita != null && (
                <ReferenceDot
                  x={pinnedRec.composite_raf_py} y={pinnedRec.gen_per_capita}
                  r={0} fill="none" stroke="none"
                  label={{ value: '▶ ' + (pinnedRec.aco_name || '').split(' ').slice(0,3).join(' '),
                    position: 'top', fontSize: 11, fontWeight: 700, fill: 'var(--teal-dark)',
                    style: { pointerEvents: 'none' } }}
                />
              )}
              {genRafTrend && (
                <Scatter data={genRafTrend} dataKey="y"
                  line={{ stroke: COLORS.navy, strokeWidth: 2, strokeDasharray: '6 3' }}
                  shape={() => null} legendType="none" />
              )}
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
            🔵 Blue = positive generated savings &nbsp;🟠 Coral = negative &nbsp;— Navy = trend
          </div>
        </ChartCard>
      </div>

      {/* ── Savings rate distribution ── */}
      <div className="chart-card">
        <div className="chart-title">Savings Rate Distribution</div>
        <div className="chart-sub">Number of ACOs in each savings rate band · PY {year}</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={distData} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} />
            <YAxis tick={{ fontSize: 11 }} width={36} />
            <Tooltip formatter={v => [v, 'ACOs']} />
            <Bar dataKey="count" name="ACOs" radius={[3,3,0,0]}>
              {distData.map((d, i) => (
                <Cell key={i} fill={d.positive ? COLORS.teal : COLORS.coral} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── ACO table ── */}
      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div className="chart-title">{fmt.num(sorted.length)} ACOs — PY {year}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Click headers to sort</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>Rows:</span>
              <div style={{ display: 'flex', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                {ROWS_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => { setRowsPerPage(opt); setTablePage(0); }}
                    style={{ padding: '4px 10px', fontSize: 12, fontFamily: 'var(--font)', cursor: 'pointer',
                      border: 'none', borderRight: '1px solid var(--gray-300)',
                      background: rowsPerPage === opt ? 'var(--navy)' : 'white',
                      color: rowsPerPage === opt ? 'white' : 'var(--gray-600)',
                      fontWeight: rowsPerPage === opt ? 600 : 400 }}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
                <th style={{ padding: '8px 10px', width: 32 }}>
                  <input type="checkbox"
                    checked={sorted.length > 0 && selectedACOIds.size === sorted.length}
                    onChange={e => e.target.checked ? selectAll() : clearSelection()}
                    title="Select all"
                    style={{ cursor: 'pointer' }} />
                </th>
                <SortHeader col="aco_name" label="ACO Name" />
                <SortHeader col="n_ab"             label="Benes" />
                <SortHeader col="sav_rate"         label="Sav Rate" />
                <SortHeader col="earn_save_loss"   label="Earned" />
                <SortHeader col="earn_per_capita"  label="Earned/Bene" />
                <SortHeader col="qual_score"       label="Quality" />
                <SortHeader col="expense_pbpm"     label="Exp PBPM" />
                <SortHeader col="composite_raf_py" label="RAF" />
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600,
                  color: 'var(--gray-600)', fontSize: 11, textTransform: 'uppercase' }}>Yrs</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600,
                  color: 'var(--gray-600)', fontSize: 11, textTransform: 'uppercase' }}>Track</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((r, i) => {
                const tenure = ACO_TENURE[r.aco_id] || '—';
                return (
                  <tr key={`${r.aco_id}-${i}`}
                    style={{ background: selectedACOIds.has(r.aco_id) ? 'var(--teal-light)' :
                      i % 2 === 1 ? 'var(--gray-50)' : 'white', borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '7px 10px', width: 32 }}>
                      <input type="checkbox" checked={selectedACOIds.has(r.aco_id)}
                        onChange={() => toggleACO(r.aco_id)} style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={{ padding: '7px 10px', maxWidth: 220,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {navigateToACO ? (
                        <>
                          <span
                            onClick={() => navigateToACO({ aco_id: r.aco_id, aco_name: r.aco_name,
                              aco_state: r.aco_state, first_year: ACO_TENURE[r.aco_id] ? 2025 - ACO_TENURE[r.aco_id] : null,
                              most_recent_year: r.performance_year, years_count: ACO_TENURE[r.aco_id] || 1 })}
                            style={{ fontWeight: 500, color: 'var(--teal-dark)', cursor: 'pointer',
                              textDecoration: 'none' }}
                            onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                            onMouseLeave={e => e.target.style.textDecoration = 'none'}
                          >
                            {r.aco_name || r.aco_id}
                          </span>
                          {pinACO && (
                          <span
                            onClick={e => { e.stopPropagation(); pinACO({ aco_id: r.aco_id, aco_name: r.aco_name, aco_state: r.aco_state }); }}
                            title="Pin this ACO across all pages"
                            style={{ marginLeft: 6, fontSize: 11, color: pinnedACO?.aco_id === r.aco_id ? 'var(--teal)' : 'var(--gray-300)',
                              cursor: 'pointer' }}
                            onMouseEnter={e => e.target.style.color = 'var(--teal)'}
                            onMouseLeave={e => e.target.style.color = pinnedACO?.aco_id === r.aco_id ? 'var(--teal)' : 'var(--gray-300)'}
                          >📌</span>
                          )}
                        </>
                      ) : (
                        <span style={{ fontWeight: 500, color: 'var(--navy)' }}>{r.aco_name || r.aco_id}</span>
                      )}
                    </td>
                    <td style={{ padding: '7px 10px' }}>{fmt.num(r.n_ab)}</td>
                    <td style={{ padding: '7px 10px', fontWeight: 600,
                      color: r.sav_rate > 0 ? 'var(--green)' : r.sav_rate < 0 ? 'var(--red)' : 'var(--gray-600)' }}>
                      {fmt.pct(r.sav_rate)}
                    </td>
                    <td style={{ padding: '7px 10px', fontWeight: 600,
                      color: r.earn_save_loss > 0 ? 'var(--green)' : r.earn_save_loss < 0 ? 'var(--red)' : 'var(--gray-600)' }}>
                      {fmt.dollars(r.earn_save_loss, 2)}
                    </td>
                    <td style={{ padding: '7px 10px',
                      color: r.earn_per_capita > 0 ? 'var(--green)' : r.earn_per_capita < 0 ? 'var(--red)' : 'var(--gray-600)' }}>
                      {r.earn_per_capita != null ? fmt.dollars(r.earn_per_capita, 2) : '—'}
                    </td>
                    <td style={{ padding: '7px 10px' }}>{r.qual_score?.toFixed(1) ?? '—'}</td>
                    <td style={{ padding: '7px 10px' }}>
                      {r.expense_pbpm != null
                        ? `$${Number(r.expense_pbpm).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '—'}
                    </td>
                    <td style={{ padding: '7px 10px' }}>{r.composite_raf_py?.toFixed(3) ?? '—'}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--gray-600)' }}>{tenure}</td>
                    <td style={{ padding: '7px 10px', fontSize: 12, color: 'var(--gray-600)' }}>
                      {normalizeTrackGroup(r)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination — only show when not displaying All */}
        {rowsPerPage !== 'All' && totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, alignItems: 'center' }}>
            <button onClick={() => setTablePage(p => Math.max(0,p-1))} disabled={tablePage===0}
              style={{ padding: '5px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--gray-300)',
                background: tablePage===0 ? 'var(--gray-100)' : 'white',
                cursor: tablePage===0 ? 'default' : 'pointer',
                color: tablePage===0 ? 'var(--gray-400)' : 'var(--navy)', fontFamily: 'var(--font)', fontSize: 13 }}>
              ← Prev
            </button>
            <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>
              Page {tablePage+1} of {totalPages} ({fmt.num(sorted.length)} ACOs)
            </span>
            <button onClick={() => setTablePage(p => Math.min(totalPages-1,p+1))} disabled={tablePage===totalPages-1}
              style={{ padding: '5px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--gray-300)',
                background: tablePage===totalPages-1 ? 'var(--gray-100)' : 'white',
                cursor: tablePage===totalPages-1 ? 'default' : 'pointer',
                color: tablePage===totalPages-1 ? 'var(--gray-400)' : 'var(--navy)', fontFamily: 'var(--font)', fontSize: 13 }}>
              Next →
            </button>
          </div>
        )}
        {rowsPerPage === 'All' && (
          <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--gray-400)' }}>
            Showing all {fmt.num(sorted.length)} ACOs
          </div>
        )}
      </div>
    </div>
  );
}
